import React, { useState } from 'react';
import {
    PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend,
    BarChart, Bar, XAxis, YAxis, CartesianGrid
} from 'recharts';
import { Droplet, Wind, Shield, CheckCircle, Wrench, Package, Disc, Cog } from 'lucide-react';
import { translations } from '../translations';

const getCatIcon = (cat, t, size = 16, color = 'white') => {
    if (!cat) return <Package size={size} color={color} />;
    if ([t.categories.oilChange].includes(cat)) return <Droplet size={size} color={color} />;
    if ([t.categories.filter, t.categories.oilFilter, t.categories.fuelFilter, t.categories.cabinFilter, t.categories.transmissionFilter, t.categories.airFilter].includes(cat)) return <Wind size={size} color={color} />;
    if ([t.categories.insurance].includes(cat)) return <Shield size={size} color={color} />;
    if ([t.categories.carInspection].includes(cat)) return <CheckCircle size={size} color={color} />;
    if ([t.categories.repairs].includes(cat)) return <Wrench size={size} color={color} />;
    if ([t.categories.maintenance].includes(cat)) return <Cog size={size} color={color} />;
    if ([t.categories.brakes, t.categories.brakePads, t.categories.brakeRotors].includes(cat)) return <Disc size={size} color={color} />;
    return <Package size={size} color={color} />;
};

const VIEWS = ['pie', 'bar'];

const Statistics = ({ expenses, categoryFilter = 'All', language = 'en', colorBlindMode = false }) => {
    const t = translations[language];
    const [viewIndex, setViewIndex] = useState(() => {
        const saved = localStorage.getItem('expenseChartType');
        return saved ? parseInt(saved, 10) : 0;
    });
    const currentView = VIEWS[viewIndex || 0];

    const filterTypes = ['oilFilter', 'fuelFilter', 'cabinFilter', 'transmissionFilter', 'airFilter'];
    const brakeTypes = ['brakePads', 'brakeRotors'];

    const filteredExpenses = categoryFilter === 'All'
        ? expenses
        : expenses.filter(e => {
            const matchedKey = Object.keys(t.categories).find(key =>
                Object.values(translations).some(lang => lang.categories && lang.categories[key] === e.category)
            );
            const catName = matchedKey ? t.categories[matchedKey] : e.category;

            if (categoryFilter === t.categories.filter) {
                const validLabels = filterTypes.map(key => t.categories[key]);
                return catName === t.categories.filter || validLabels.includes(catName);
            }
            if (categoryFilter === t.categories.brakes) {
                return catName.startsWith(t.categories.brakes);
            }
            return catName.startsWith(categoryFilter);
        });

    const data = filteredExpenses.reduce((acc, curr) => {
        const amt = Number(curr.amount) || 0;
        let catName = curr.category;

        // Find the generic key for this category across any language
        const matchedKey = Object.keys(t.categories).find(key =>
            Object.values(translations).some(lang => lang.categories && lang.categories[key] === curr.category)
        );
        if (matchedKey) catName = t.categories[matchedKey];

        // Handle filter stacking
        const validFilterLabels = filterTypes.map(key => t.categories[key]);
        if (validFilterLabels.includes(catName)) {
            catName = t.categories.filter;
        } else if (catName && catName.startsWith(t.categories.brakes)) {
            catName = t.categories.brakes;
        }

        const found = acc.find(item => item.name === catName);
        if (found) {
            found.value += amt;
        } else {
            acc.push({ name: catName, value: amt });
        }
        return acc;
    }, []);

    const COLORS = ['#738ADB', '#22c55e', '#ef4444', '#f59e0b', '#ec4899', '#4f46e5'];

    const totalAmount = data.reduce((sum, item) => sum + item.value, 0);

    const cycleView = () => {
        setViewIndex(i => {
            const next = (i + 1) % VIEWS.length;
            localStorage.setItem('expenseChartType', next.toString());
            return next;
        });
    };

    const viewIcon = currentView === 'pie'
        ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M9 21V9" />
            </svg>
        ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21.21 15.89A10 10 0 1 1 8 2.83" /><path d="M22 12A10 10 0 0 0 12 2v10z" />
            </svg>
        );

    const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
        if (!colorBlindMode || percent < 0.05) return null;
        const RADIAN = Math.PI / 180;
        const radius = innerRadius + (outerRadius - innerRadius) * 0.7;
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);
        return (
            <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontWeight="800" fontSize="1.1rem" style={{ textShadow: '0px 1px 3px rgba(0,0,0,0.5)' }}>
                {String.fromCharCode(65 + index)}
            </text>
        );
    };

    const tooltipStyle = {
        contentStyle: {
            backgroundColor: 'var(--modal-bg)',
            borderColor: 'var(--border-color)',
            borderRadius: 'var(--radius-md)',
            fontSize: '0.875rem',
            color: 'var(--header-text)',
        },
        itemStyle: { color: 'var(--header-text)' },
        labelStyle: { color: 'var(--header-text)', fontWeight: 600 },
    };

    const renderCustomBarLabel = (props) => {
        const { x, y, width, height, index } = props;
        const barData = data[index];
        if (!barData) return null;
        const iconSize = 18;
        const cx = x + width / 2;
        const fitsInside = height > 28;
        const cy = fitsInside ? y + height - 28 : y - 24;
        const color = fitsInside ? '#ffffff' : 'var(--text-secondary)';

        return (
            <g transform={`translate(${cx - iconSize / 2}, ${cy})`}>
                {getCatIcon(barData.name, t, iconSize, color)}
            </g>
        );
    };

    return (
        <div className="statistics-container">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <h2 className="section-title" style={{ margin: 0 }}>{t.expensesBreakdown}</h2>
                {data.length > 0 && (
                    <button
                        onClick={cycleView}
                        title={currentView === 'pie' ? 'Switch to Bar Chart' : 'Switch to Pie Chart'}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.35rem',
                            padding: '0.4rem 0.75rem',
                            borderRadius: '999px',
                            border: '1.5px solid var(--border-color)',
                            background: 'var(--bg-secondary)',
                            color: 'var(--text-secondary)',
                            fontSize: '0.78rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                        }}
                    >
                        {viewIcon}
                        {currentView === 'pie' ? 'Bar' : 'Pie'}
                    </button>
                )}
            </div>

            {data.length > 0 ? (
                <div className="chart-card">
                    {currentView === 'pie' && (
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie
                                    data={data}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    label={renderCustomizedLabel}
                                    outerRadius={80}
                                    fill="#8884d8"
                                    dataKey="value"
                                >
                                    {data.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} stroke="var(--bg-card)" strokeWidth={2} />
                                    ))}
                                </Pie>
                                <Tooltip {...tooltipStyle} />
                                <Legend
                                    wrapperStyle={{ fontSize: '0.9rem', paddingTop: '20px' }}
                                    iconSize={0}
                                    formatter={(value, entry) => {
                                        const percent = totalAmount > 0 ? ((entry.payload.value / totalAmount) * 100).toFixed(0) : 0;
                                        const idx = data.findIndex(d => d.name === value);
                                        const letter = idx !== -1 ? String.fromCharCode(65 + idx) : '';
                                        return (
                                            <span style={{ color: entry.color, display: 'inline-flex', alignItems: 'center', gap: '6px', transform: 'translateY(4px)', marginLeft: '-8px' }}>
                                                {colorBlindMode && <span style={{ fontWeight: 800, marginRight: '-2px' }}>{letter}</span>}
                                                {getCatIcon(value, t, 18, entry.color)}
                                                <span style={{ fontWeight: 600 }}>{percent}%</span>
                                            </span>
                                        );
                                    }}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                    )}

                    {currentView === 'bar' && (
                        <ResponsiveContainer width="100%" height={300} style={{ pointerEvents: 'none' }}>
                            <BarChart data={data} margin={{ top: 25, right: 10, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" vertical={false} />
                                <XAxis
                                    dataKey="name"
                                    tick={{ fill: 'var(--text-secondary)', fontSize: '0.72rem' }}
                                    tickLine={false}
                                    axisLine={false}
                                />
                                <YAxis
                                    tick={{ fill: 'var(--text-secondary)', fontSize: '0.72rem' }}
                                    width={45}
                                    axisLine={false}
                                    tickLine={false}
                                    tickFormatter={(val) => val >= 1000 ? `$${(val / 1000).toFixed(val % 1000 === 0 ? 0 : 1)}k` : `$${val}`}
                                />
                                <Bar dataKey="value" radius={[6, 6, 0, 0]} label={renderCustomBarLabel}>
                                    {data.map((entry, index) => (
                                        <Cell key={`bar-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </div>
            ) : (
                <div className="empty-card">
                    <p>{t.noExpensesData}</p>
                </div>
            )}
        </div>
    );
};

export default Statistics;
